<template>
  <!-- Mobile Overlay -->
  <ClAnimatePresence>
    <ClMotion
      v-if="isMobile && !isCollapsed"
      key="sidebar-overlay"
      preset="fade"
      as="div"
      class="sidebar-overlay"
      @click="toggleSidebar"
    />
  </ClAnimatePresence>
  
  <aside
    class="modern-sidebar font-sans text-foreground"
    :class="{
      collapsed: isCollapsed,
      'mobile-open': isMobile && !isCollapsed,
      'sidebar-layout-stack': sidebarLayout === 'stack',
      'sidebar-layout-compact': sidebarLayout === 'compact',
      'sidebar-layout-sections': sidebarLayout === 'sections',
      'sidebar-layout-dock': sidebarLayout === 'dock',
      'sidebar-layout-cards': sidebarLayout === 'cards',
      [`sidebar-style-${sidebarStyle}`]: true,
      'sidebar-profile-end': profileSlot === 'footer',
    }"
  >
    <!-- Header Section -->
    <div class="sidebar-header" v-if="!isCollapsed">
      <div class="logo-container">
        <BrandLogoMark
          :name="sidebarBrandName"
          :logo-url="sidebarBrandLogoUrl"
          :primary-color="brandPrimaryColor"
          :tenant-host="isTenantHost"
          size="md"
          layout="horizontal"
          class="sidebar-brand-mark"
        />
      </div>
      <!-- Close button for mobile -->
      <button 
        v-if="isMobile" 
        class="mobile-close-btn" 
        @click="toggleSidebar" 
        aria-label="Close Sidebar"
      >
        <i class="fas fa-times"></i>
      </button>
    </div>
    
    <!-- Collapsed Header -->
    <div class="sidebar-header-collapsed" v-else>
      <button 
        class="expand-btn" 
        @click="toggleSidebar" 
        :aria-label="isCardsNav ? 'Open apps' : 'Expand Sidebar'"
        :title="isCardsNav ? 'Open apps' : 'Expand Sidebar'"
      >
        <i class="fas fa-bars"></i>
      </button>
    </div>
    
    <!-- User Profile Section (top of rail; footer identity lives in sidebar-footer) -->
    <div
      class="sidebar-profile"
      :class="`sidebar-profile--${sidebarProfileStyle}`"
      v-if="!isCollapsed && currentUser && showProfileInSidebar && profileSlot !== 'footer'"
    >
      <div class="profile-avatar">
        <i class="fas fa-user"></i>
      </div>
      <div class="profile-info">
        <div class="profile-name">{{ getUserDisplayName }}</div>
        <div class="profile-role">{{ getUserRole }}</div>
        <div v-if="assignedStoresLabel" class="profile-store" :title="assignedStoresTitle">
          <i class="fas fa-store" aria-hidden="true"></i>
          <span class="profile-store-name">{{ assignedStoresLabel }}</span>
        </div>
      </div>
    </div>
    
    <!-- Navigation Menu -->
    <nav class="sidebar-nav">
      <ul class="nav-list">
        <template v-for="row in visibleRows" :key="row.key">
          <li v-if="row.type === 'item'" class="nav-item">
            <router-link
              :to="row.item.path"
              class="nav-link"
              :class="{ 'active': isActiveRoute(row.item.path) }"
              :aria-label="row.item.label"
              @click="closeNavGroups()"
            >
              <div class="nav-icon">
                <i :class="row.item.icon"></i>
                <span
                  v-if="isCollapsed && navBadge(row.item.path)"
                  class="nav-alert-badge nav-alert-badge--on-icon"
                  aria-hidden="true"
                >{{ navBadge(row.item.path) }}</span>
              </div>
              <span class="nav-label" v-if="!isCollapsed">{{ row.item.label }}</span>
              <span
                v-else-if="showRailCaptions"
                class="nav-label nav-label--rail"
              >{{ row.item.label }}</span>
              <span
                v-if="!isCollapsed && navBadge(row.item.path)"
                class="nav-alert-badge"
                :aria-label="navBadgeLabel(row.item.path)"
              >{{ navBadge(row.item.path) }}</span>
              <div class="nav-indicator" v-if="isActiveRoute(row.item.path)"></div>
              <transition name="tooltip">
                <div class="nav-tooltip" v-if="isCollapsed">
                  <span>{{ row.item.label }}</span>
                </div>
              </transition>
            </router-link>
          </li>
          <li
            v-else
            class="nav-group"
            :class="{ 'is-open': isGroupOpen(row.group.id), 'has-active': groupHasActive(row.group) }"
          >
            <button
              type="button"
              class="nav-group__toggle"
              :class="{
                'is-active': groupHasActive(row.group),
                'is-kicker': isSectionsLayout,
              }"
              :aria-expanded="isSectionsLayout || isGroupOpen(row.group.id) ? 'true' : 'false'"
              :aria-label="row.group.label"
              @click="!isSectionsLayout && toggleGroup(row.group.id)"
            >
              <div class="nav-icon">
                <i :class="row.group.icon"></i>
                <span
                  v-if="sidebarLayout === 'stack' && !groupShowsChildren(row.group.id) && groupBadge(row.group)"
                  class="nav-alert-badge nav-alert-badge--on-icon"
                  aria-hidden="true"
                >{{ groupBadge(row.group) }}</span>
                <span
                  v-else-if="sidebarLayout === 'stack' && groupShowsChildren(row.group.id) && groupBadgeCount(row.group)"
                  class="nav-alert-dot nav-alert-dot--on-icon"
                  aria-hidden="true"
                />
              </div>
              <span class="nav-label">{{ row.group.label }}</span>
              <span
                v-if="!groupShowsChildren(row.group.id) && groupBadge(row.group)"
                class="nav-alert-badge"
                :aria-label="groupBadgeLabel(row.group)"
              >{{ groupBadge(row.group) }}</span>
              <span
                v-else-if="groupShowsChildren(row.group.id) && groupBadgeCount(row.group)"
                class="nav-alert-dot"
                :aria-label="groupBadgeLabel(row.group)"
              />
              <i
                v-if="!isSectionsLayout"
                class="fas fa-chevron-down nav-group__chevron"
                :class="{ 'is-open': isGroupOpen(row.group.id) }"
                aria-hidden="true"
              />
            </button>
            <ul v-show="isSectionsLayout || isGroupOpen(row.group.id)" class="nav-group__items">
              <li v-for="item in row.group.items" :key="item.path" class="nav-item nav-item--nested">
                <router-link
                  :to="item.path"
                  class="nav-link"
                  :class="{ 'active': isActiveRoute(item.path) }"
                  :aria-label="item.label"
                >
                  <div class="nav-icon">
                    <i :class="item.icon"></i>
                  </div>
                  <span class="nav-label">{{ item.label }}</span>
                  <span
                    v-if="navBadge(item.path)"
                    class="nav-alert-badge"
                    :aria-label="navBadgeLabel(item.path)"
                  >{{ navBadge(item.path) }}</span>
                  <div class="nav-indicator" v-if="isActiveRoute(item.path)"></div>
                </router-link>
              </li>
            </ul>
          </li>
        </template>
      </ul>
    </nav>
    
    <!-- Footer Actions -->
    <div class="sidebar-footer" :class="{ 'sidebar-footer--identity': showFooterIdentity }">
      <div v-if="showFooterIdentity" class="sidebar-footer-identity">
        <div class="profile-avatar">
          <i class="fas fa-user"></i>
        </div>
        <div class="profile-info">
          <div class="profile-name">{{ getUserDisplayName }}</div>
          <div class="profile-role">{{ getUserRole }}</div>
          <div v-if="assignedStoresLabel" class="profile-store" :title="assignedStoresTitle">
            <i class="fas fa-store" aria-hidden="true"></i>
            <span class="profile-store-name">{{ assignedStoresLabel }}</span>
          </div>
        </div>
      </div>
      <!-- Settings Button (optional) -->
      <button 
        class="footer-btn settings-btn d-none"
        @click="goToSettings"
        :aria-label="isCollapsed ? 'Settings' : ''"
      >
        <div class="footer-icon">
          <i class="fas fa-cog"></i>
        </div>
        <span class="footer-label" v-if="!isCollapsed">Settings</span>
        
        <transition name="tooltip">
          <div class="nav-tooltip" v-if="isCollapsed">
            <span>Settings</span>
          </div>
        </transition>
      </button>
      
      <!-- Logout Button -->
      <button 
        class="footer-btn logout-btn"
        :class="{ 'logout-btn--icon': showFooterIdentity }"
        @click="handleLogout"
        :aria-label="showFooterIdentity || isCollapsed ? 'Logout' : ''"
        :title="showFooterIdentity ? 'Logout' : undefined"
      >
        <div class="footer-icon">
          <i class="fas fa-sign-out-alt"></i>
        </div>
        <span
          class="footer-label"
          :class="{ 'footer-label--rail': showRailCaptions }"
          v-if="(!isCollapsed && !showFooterIdentity) || showRailCaptions"
        >Logout</span>
        
        <transition name="tooltip">
          <div class="nav-tooltip" v-if="isCollapsed || showFooterIdentity">
            <span>Logout</span>
          </div>
        </transition>
      </button>
    </div>
  </aside>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import eventBus from '../utils/eventBus'
import api from '@/utils/api'
import {
  INBOX_ACTIVITY_EVENT,
  INBOX_BADGE_POLL_MS,
  INBOX_PATH,
  isInboxRoute,
  readInboxLastSeen,
  writeInboxLastSeen,
} from '@/utils/inboxLive'
import ClAnimatePresence from './ds/ClAnimatePresence.vue'
import ClMotion from './ds/ClMotion.vue'
import {
  getSidebarNavSections,
  isPlatformOperator,
  sidebarGroupIdForPath,
} from '@/composables/usePermissions'
import {
  DEFAULT_CLINCH_THEME,
  isCardsNavLayout,
  loadBrandThemeFromStorage,
  resolveProfilePlacement,
  resolveShellBrandName,
} from '@/theme/brandTheme'
import { hasCustomBrandLogo } from '@/theme/brandLogo'
import { isTenantWorkspaceHost } from '@/utils/saasPlatform'
import BrandLogoMark from '@/components/brand/BrandLogoMark.vue'

const ALERTS_PATH = '/manage/alerts'
const ALERTS_POLL_MS = 5 * 60 * 1000
const WALLET_PATH = '/manage/store-wallet'
const WALLET_POLL_MS = 60 * 1000
const PAYMENT_PROOFS_PATH = '/manage/payment-proofs'
const EFT_PROOF_POLL_MS = 60 * 1000

export default {
  name: 'Sidebar',
  components: { ClAnimatePresence, ClMotion, BrandLogoMark },
  created() {
    this.onBrandThemeChanged()
    if (typeof window !== 'undefined') {
      window.addEventListener('brand-theme-changed', this.onBrandThemeChanged)
    }
  },
  data() {
    const brand = loadBrandThemeFromStorage()
    return {
      isCollapsed: false,
      isMobile: false,
      brandLogoUrl: brand.logoUrl || DEFAULT_CLINCH_THEME.logoUrl,
      brandAppName: brand.appName || DEFAULT_CLINCH_THEME.appName,
      brandPrimaryColor: brand.colors?.primary || DEFAULT_CLINCH_THEME.colors.primary,
      sidebarStyle: brand.sidebar?.style || DEFAULT_CLINCH_THEME.sidebar.style,
      sidebarLayout: brand.sidebar?.layout || DEFAULT_CLINCH_THEME.sidebar.layout,
      sidebarProfileStyle:
        brand.sidebar?.profileStyle || DEFAULT_CLINCH_THEME.sidebar.profileStyle || 'panel',
      sidebarProfilePosition:
        brand.sidebar?.profilePosition || DEFAULT_CLINCH_THEME.sidebar.profilePosition || 'auto',
      openGroupIds: {},
      alertsOpenCount: 0,
      alertsPollTimer: null,
      walletOpenCount: 0,
      walletPollTimer: null,
      inboxUnreadCount: 0,
      inboxPollTimer: null,
      inboxOpenedAt: null,
      eftProofsCount: 0,
      eftProofsPollTimer: null,
    }
  },
  computed: {
    ...mapGetters(['currentUser', 'isAuthenticated']),

    profilePlacement() {
      return resolveProfilePlacement({
        layout: this.sidebarLayout,
        profilePosition: this.sidebarProfilePosition,
      })
    },
    showProfileInSidebar() {
      return this.profilePlacement.showInSidebar
    },
    profileSlot() {
      return this.profilePlacement.slot || 'top'
    },
    isSectionsLayout() {
      return this.sidebarLayout === 'sections'
    },
    isCardsNav() {
      return isCardsNavLayout(this.sidebarLayout)
    },
    showRailCaptions() {
      return this.isCardsNav && this.isCollapsed
    },
    showFooterIdentity() {
      return (
        !this.isCollapsed &&
        Boolean(this.currentUser) &&
        this.showProfileInSidebar &&
        this.profileSlot === 'footer'
      )
    },    
    isAdmin() {
      if (!this.isAuthenticated || !this.currentUser) {
        return false
      }
      return isPlatformOperator(this.currentUser)
    },
    
    getUserDisplayName() {
      if (!this.currentUser) return 'User'
      return this.currentUser.name || 
             this.currentUser.email?.split('@')[0] || 
             'User'
    },
    
    getUserRole() {
      return this.currentUser.user_type?.name || this.currentUser.user_type?.code || 'User'
    },

    assignedStoreNames() {
      const stores = this.currentUser?.assigned_stores
      if (!Array.isArray(stores)) return []
      return stores.map((store) => store?.business_name).filter(Boolean)
    },

    assignedStoresLabel() {
      const names = this.assignedStoreNames
      if (!names.length) return ''
      if (names.length === 1) return names[0]
      return `${names[0]} +${names.length - 1}`
    },

    assignedStoresTitle() {
      return this.assignedStoreNames.join(', ')
    },

    navSections() {
      return getSidebarNavSections(this.currentUser)
    },

    visibleRows() {
      if (this.isCollapsed) {
        return this.navSections.flatItems.map((item) => ({
          type: 'item',
          key: item.path,
          item,
        }))
      }
      return this.navSections.rows
    },

    isTenantHost() {
      return isTenantWorkspaceHost()
    },

    /** Custom uploaded/theme logo when set; otherwise BrandLogoMark uses mark + name. */
    sidebarBrandLogoUrl() {
      if (hasCustomBrandLogo(this.brandLogoUrl)) return this.brandLogoUrl
      if (!this.isTenantHost) return this.brandLogoUrl || DEFAULT_CLINCH_THEME.logoUrl
      return ''
    },

    /** Custom app name, else tenant/domain label on workspace Hosts (not "Clinch"). */
    sidebarBrandName() {
      return resolveShellBrandName(this.brandAppName, { tenantHost: this.isTenantHost })
    },

    hasAlertsLink() {
      return this.navSections.flatItems.some((item) => item.path === ALERTS_PATH)
    },

    hasInboxLink() {
      return this.navSections.flatItems.some((item) => item.path === INBOX_PATH)
    },

    hasWalletLink() {
      return this.navSections.flatItems.some((item) => item.path === WALLET_PATH)
    },

    hasEftProofsLink() {
      return this.navSections.flatItems.some((item) => item.path === PAYMENT_PROOFS_PATH)
    },
  },
  
  methods: {
    ...mapActions(['logout']),

    onBrandThemeChanged(event) {
      const theme = event?.detail?.theme || loadBrandThemeFromStorage()
      this.brandLogoUrl = theme.logoUrl || DEFAULT_CLINCH_THEME.logoUrl
      this.brandAppName = theme.appName || DEFAULT_CLINCH_THEME.appName
      this.brandPrimaryColor = theme.colors?.primary || DEFAULT_CLINCH_THEME.colors.primary
      this.sidebarStyle = theme.sidebar?.style || DEFAULT_CLINCH_THEME.sidebar.style
      this.sidebarLayout =
        theme.sidebar?.layout || DEFAULT_CLINCH_THEME.sidebar.layout
      this.sidebarProfileStyle =
        theme.sidebar?.profileStyle || DEFAULT_CLINCH_THEME.sidebar.profileStyle || 'panel'
      this.sidebarProfilePosition =
        theme.sidebar?.profilePosition || DEFAULT_CLINCH_THEME.sidebar.profilePosition || 'auto'
      if (isCardsNavLayout(this.sidebarLayout)) {
        this.lockCardsCollapsed()
      }
    },
    
    isActiveRoute(itemPath) {
      const currentPath = this.$route.path
      const tab = String(this.$route.query.tab || '')
      const onCampaigns =
        (currentPath === '/manage/templates' && (tab === 'campaigns' || tab === 'broadcasts'))
        || currentPath.startsWith('/manage/templates/broadcasts')

      if (
        itemPath === '/manage/templates?tab=broadcasts'
        || itemPath === '/manage/templates?tab=campaigns'
      ) {
        return onCampaigns
      }
      if (itemPath === '/manage/templates') {
        if (onCampaigns) return false
        return currentPath === '/manage/templates' || currentPath.startsWith('/manage/templates/')
      }

      if (itemPath.includes('?')) {
        const [path, search] = itemPath.split('?')
        const wanted = new URLSearchParams(search)
        if (currentPath !== path) return false
        for (const [key, value] of wanted) {
          if (String(this.$route.query[key] ?? '') !== value) return false
        }
        return true
      }
      
      // Exact match for dashboard
      if (itemPath === '/dashboard') {
        return currentPath === '/dashboard'
      }
      
      // Exact match
      if (currentPath === itemPath) {
        return true
      }
      
      // Match sub-routes
      if (currentPath.startsWith(itemPath + '/')) {
        return true
      }
      
      return false
    },

    groupHasActive(group) {
      return (group.items || []).some((item) => this.isActiveRoute(item.path))
    },

    isGroupOpen(groupId) {
      return this.openGroupIds[groupId] === true
    },

    closeNavGroups() {
      this.openGroupIds = {}
    },

    toggleGroup(groupId) {
      const willOpen = !this.openGroupIds[groupId]
      // Accordion: only one folder open at a time
      this.openGroupIds = willOpen ? { [groupId]: true } : {}
    },

    syncOpenGroupFromRoute() {
      const fromRoute = sidebarGroupIdForPath(this.$route.path, this.navSections)
      // Keep only the folder that owns the current route; close folders on standalone pages
      this.openGroupIds = fromRoute ? { [fromRoute]: true } : {}
    },
    
    checkMobile() {
      this.isMobile = window.innerWidth <= 768
    },
    
    toggleSidebar() {
      if (this.isCardsNav) {
        eventBus.emit('toggle-nav-cards')
        return
      }
      this.isCollapsed = !this.isCollapsed
      this.syncSidebarState()
    },
    lockCardsCollapsed() {
      if (!this.isCollapsed) {
        this.isCollapsed = true
        this.syncSidebarState()
      }
    },
    syncSidebarState() {
      this.$emit('sidebar-toggle', this.isCollapsed)
      eventBus.emit('sidebar-state-changed', this.isCollapsed)
      if (!this.isMobile) {
        localStorage.setItem('sidebarCollapsed', this.isCollapsed)
      }
    },
    
    goToSettings() {
      this.$router.push('/settings')
    },

    navBadgeCount(path) {
      if (path === ALERTS_PATH) return this.alertsOpenCount
      if (path === INBOX_PATH) return this.inboxUnreadCount
      if (path === WALLET_PATH) return this.walletOpenCount
      if (path === PAYMENT_PROOFS_PATH) return this.eftProofsCount
      return 0
    },

    navBadge(path) {
      const count = this.navBadgeCount(path)
      if (!count) return ''
      return count > 99 ? '99+' : String(count)
    },

    navBadgeLabel(path) {
      const count = this.navBadgeCount(path)
      if (path === INBOX_PATH) {
        return `${count} ${count === 1 ? 'chat' : 'chats'} with new customer messages`
      }
      if (path === WALLET_PATH) {
        return `${count} open payout ${count === 1 ? 'request' : 'requests'}`
      }
      if (path === PAYMENT_PROOFS_PATH) {
        return `${count} proof ${count === 1 ? 'of payment' : 'of payments'} waiting`
      }
      return `${count} new stock management issues`
    },

    groupShowsChildren(groupId) {
      return this.isSectionsLayout || this.isGroupOpen(groupId)
    },

    groupBadgeCount(group) {
      return (group?.items || []).reduce(
        (sum, item) => sum + this.navBadgeCount(item.path),
        0,
      )
    },

    groupBadge(group) {
      const count = this.groupBadgeCount(group)
      if (!count) return ''
      return count > 99 ? '99+' : String(count)
    },

    groupBadgeLabel(group) {
      const parts = []
      for (const item of group?.items || []) {
        if (!this.navBadgeCount(item.path)) continue
        parts.push(this.navBadgeLabel(item.path))
      }
      return parts.join('. ')
    },

    isOnInbox() {
      return isInboxRoute(this.$route?.path)
    },

    markInboxSeen() {
      writeInboxLastSeen(this.currentUser)
      this.inboxUnreadCount = 0
    },

    async loadInboxBadge() {
      if (!this.hasInboxLink) {
        this.inboxUnreadCount = 0
        return
      }
      let since = null
      if (this.isOnInbox()) {
        if (!this.inboxOpenedAt) this.inboxOpenedAt = new Date().toISOString()
        since = this.inboxOpenedAt
      } else {
        this.inboxOpenedAt = null
        since = readInboxLastSeen(this.currentUser)
      }
      try {
        const { data } = await api.get('/admin/inbox/unread-count/', {
          params: since ? { since } : {},
        })
        this.inboxUnreadCount = Number(data?.count) || 0
      } catch (err) {
        console.warn('[Sidebar] inbox badge unavailable', err?.response?.status || err)
      }
    },

    startInboxPolling() {
      clearInterval(this.inboxPollTimer)
      this.inboxPollTimer = null
      this.loadInboxBadge()
      if (this.hasInboxLink) {
        this.inboxPollTimer = setInterval(this.loadInboxBadge, INBOX_BADGE_POLL_MS)
      }
    },

    onInboxActivity() {
      this.loadInboxBadge()
    },

    onDocumentVisible() {
      if (document.visibilityState === 'visible') this.loadInboxBadge()
    },

    onAlertsChanged(summary) {
      this.alertsOpenCount = Number(summary?.open) || 0
    },

    async loadAlertsBadge() {
      if (!this.hasAlertsLink) {
        this.alertsOpenCount = 0
        return
      }
      try {
        const { data } = await api.get('/businesses/alerts/summary/')
        this.onAlertsChanged(data)
      } catch (err) {
        // Badge is optional — never break navigation over it.
        console.warn('[Sidebar] alerts badge unavailable', err?.response?.status || err)
      }
    },

    onStoreWalletChanged(totals) {
      this.walletOpenCount = Number(totals?.open_withdrawals_count) || 0
    },

    async loadWalletBadge() {
      if (!this.hasWalletLink) {
        this.walletOpenCount = 0
        return
      }
      try {
        const { data } = await api.get('/wallets/store-wallets/')
        this.onStoreWalletChanged(data?.totals)
      } catch (err) {
        console.warn('[Sidebar] wallet badge unavailable', err?.response?.status || err)
      }
    },

    startWalletPolling() {
      clearInterval(this.walletPollTimer)
      this.walletPollTimer = null
      this.loadWalletBadge()
      if (this.hasWalletLink) {
        this.walletPollTimer = setInterval(this.loadWalletBadge, WALLET_POLL_MS)
      }
    },

    startAlertsPolling() {
      clearInterval(this.alertsPollTimer)
      this.alertsPollTimer = null
      this.loadAlertsBadge()
      if (this.hasAlertsLink) {
        this.alertsPollTimer = setInterval(this.loadAlertsBadge, ALERTS_POLL_MS)
      }
    },

    async loadEftProofsBadge() {
      if (!this.hasEftProofsLink) {
        this.eftProofsCount = 0
        return
      }
      try {
        const { data } = await api.get('/notifications/summary/')
        this.eftProofsCount = Number(data?.eft_proofs) || 0
      } catch (err) {
        console.warn('[Sidebar] EFT proof badge unavailable', err?.response?.status || err)
      }
    },

    startEftProofsPolling() {
      clearInterval(this.eftProofsPollTimer)
      this.eftProofsPollTimer = null
      this.loadEftProofsBadge()
      if (this.hasEftProofsLink) {
        this.eftProofsPollTimer = setInterval(this.loadEftProofsBadge, EFT_PROOF_POLL_MS)
      }
    },
    
    async handleLogout() {
      try {
        await this.logout()
        this.$router.push('/')
      } catch (error) {
        console.error('Logout error:', error)
        this.$router.push('/')
      }
    }
  },
  
  mounted() {
    eventBus.on('toggle-sidebar', this.toggleSidebar)
    eventBus.on('store-alerts-changed', this.onAlertsChanged)
    eventBus.on('store-wallet-changed', this.onStoreWalletChanged)
    eventBus.on(INBOX_ACTIVITY_EVENT, this.onInboxActivity)
    document.addEventListener('visibilitychange', this.onDocumentVisible)
    this.startAlertsPolling()
    this.startInboxPolling()
    this.startWalletPolling()
    this.startEftProofsPolling()
    
    this.checkMobile()
    window.addEventListener('resize', this.checkMobile)
    this.syncOpenGroupFromRoute()

    // Load saved state (desktop only)
    if (!this.isMobile) {
      const savedState = localStorage.getItem('sidebarCollapsed')
      if (savedState !== null) {
        this.isCollapsed = savedState === 'true'
        eventBus.emit('sidebar-state-changed', this.isCollapsed)
      }
    } else {
      // Mobile: start collapsed
      this.isCollapsed = true
      eventBus.emit('sidebar-state-changed', this.isCollapsed)
    }
    if (this.isCardsNav) {
      this.lockCardsCollapsed()
    }
  },
  
  beforeUnmount() {
    eventBus.off('toggle-sidebar', this.toggleSidebar)
    eventBus.off('store-alerts-changed', this.onAlertsChanged)
    eventBus.off('store-wallet-changed', this.onStoreWalletChanged)
    eventBus.off(INBOX_ACTIVITY_EVENT, this.onInboxActivity)
    document.removeEventListener('visibilitychange', this.onDocumentVisible)
    clearInterval(this.alertsPollTimer)
    clearInterval(this.inboxPollTimer)
    clearInterval(this.walletPollTimer)
    clearInterval(this.eftProofsPollTimer)
    window.removeEventListener('resize', this.checkMobile)
    window.removeEventListener('brand-theme-changed', this.onBrandThemeChanged)
  },
  
  watch: {
    isCollapsed(newVal) {
      if (!this.isMobile) {
        localStorage.setItem('sidebarCollapsed', newVal)
      }
      if (!newVal) {
        this.syncOpenGroupFromRoute()
      }
    },
    
    isMobile(newVal) {
      if (newVal) {
        this.isCollapsed = true
        eventBus.emit('sidebar-state-changed', this.isCollapsed)
      }
    },

    isCardsNav(on) {
      if (on) this.lockCardsCollapsed()
    },

    currentUser() {
      this.syncOpenGroupFromRoute()
    },

    hasAlertsLink() {
      this.startAlertsPolling()
    },

    hasInboxLink() {
      this.startInboxPolling()
    },

    hasWalletLink() {
      this.startWalletPolling()
    },

    hasEftProofsLink() {
      this.startEftProofsPolling()
    },
    
    '$route'(to, from) {
      this.syncOpenGroupFromRoute()
      if (this.hasInboxLink && to?.path !== from?.path) {
        const wasOnInbox = isInboxRoute(from?.path)
        if (this.isOnInbox()) {
          this.inboxOpenedAt = new Date().toISOString()
          this.markInboxSeen()
        } else if (wasOnInbox) {
          this.inboxOpenedAt = null
          this.markInboxSeen()
        }
        this.loadInboxBadge()
      }
      // Close sidebar on mobile when navigating to a new page
      if (this.isMobile && !this.isCollapsed) {
        this.isCollapsed = true
        eventBus.emit('sidebar-state-changed', this.isCollapsed)
      }
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700&display=swap');

.modern-sidebar {
  position: fixed;
  left: 0;
  top: 0;
  height: 100vh;
  width: var(--sidebar-width, 248px);
  background: linear-gradient(
    180deg,
    var(--sidebar-bg-start, #ffffff) 0%,
    var(--sidebar-bg-mid, #f8fafc) 45%,
    var(--sidebar-bg-end, #f1f5f9) 100%
  );
  color: var(--sidebar-fg, var(--clinch-navy));
  border-right: 1px solid var(--sidebar-border, rgba(var(--clinch-navy-rgb), 0.08));
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 4px 0 24px rgba(var(--clinch-navy-rgb), 0.06);
  font-family: 'Outfit', sans-serif;
}

.sidebar-header {
  order: 1;
}

.sidebar-header-collapsed {
  order: 1;
}

.sidebar-profile {
  order: 2;
}

.sidebar-nav {
  order: 3;
}

.sidebar-footer {
  order: 5;
}

.modern-sidebar.collapsed {
  width: var(--sidebar-collapsed-width, 80px);
}

.modern-sidebar.sidebar-layout-cards,
.modern-sidebar.sidebar-layout-cards.collapsed {
  border-right: 2px solid var(--clinch-primary, #33b5ff);
  box-shadow: none;
}

/* Sidebar Header — fixed height matches Navigation.vue top bar (see --navbar-height) */
.sidebar-header {
  position: relative;
  box-sizing: border-box;
  height: var(--navbar-height, 86px);
  min-height: var(--navbar-height, 86px);
  max-height: var(--navbar-height, 86px);
  padding: 4px 0.75rem;
  border-bottom: 1px solid var(--sidebar-border, rgba(var(--clinch-navy-rgb), 0.08));
  display: flex;
  align-items: stretch;
  justify-content: center;
  flex-shrink: 0;
}

.logo-container {
  box-sizing: border-box;
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  padding: 0.125rem 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 0;
  box-shadow: none;
}

.sidebar-brand-mark {
  width: auto;
  max-width: 100%;
  min-width: 0;
}

.sidebar-logo {
  height: auto;
  width: auto;
  max-width: 96%;
  max-height: 100%;
  object-fit: contain;
  transition: all 0.3s ease;
}

.mobile-close-btn {
  position: absolute;
  top: 50%;
  right: 0.5rem;
  transform: translateY(-50%);
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(var(--clinch-navy-rgb), 0.12);
  border-radius: 10px;
  color: var(--clinch-navy);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1rem;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(var(--clinch-navy-rgb), 0.06);
}

.mobile-close-btn:hover {
  background: rgba(var(--clinch-primary-rgb), 0.12);
  color: var(--clinch-blue-primary);
  border-color: rgba(var(--clinch-primary-rgb), 0.35);
  transform: translateY(-50%) scale(1.05);
}

/* Collapsed Header */
.sidebar-header-collapsed {
  box-sizing: border-box;
  height: var(--navbar-height, 86px);
  min-height: var(--navbar-height, 86px);
  max-height: var(--navbar-height, 86px);
  padding: 0 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-bottom: 1px solid var(--sidebar-border, rgba(var(--clinch-navy-rgb), 0.08));
}

.expand-btn {
  width: 44px;
  height: 44px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(var(--clinch-navy-rgb), 0.12);
  border-radius: 12px;
  color: var(--clinch-navy);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1.25rem;
  box-shadow: 0 2px 8px rgba(var(--clinch-navy-rgb), 0.06);
}

.expand-btn:hover {
  background: rgba(var(--clinch-primary-rgb), 0.12);
  color: var(--clinch-blue-primary);
  border-color: rgba(var(--clinch-primary-rgb), 0.35);
  transform: scale(1.05);
  box-shadow: 0 8px 20px rgba(var(--clinch-primary-rgb), 0.15);
}

/* Match Navigation.vue: top bar uses 76px below 480px */
@media (max-width: 480px) {
  .sidebar-header,
  .sidebar-header-collapsed {
    height: 76px;
    min-height: 76px;
    max-height: 76px;
  }
}

/* Profile Section — default = panel (full-bleed strip, rounded-square avatar) */
.sidebar-profile {
  margin: 0;
  padding: 0.875rem 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.625rem;
  border-bottom: 1px solid var(--sidebar-border, rgba(var(--clinch-navy-rgb), 0.08));
  background: var(--sidebar-hover-bg, rgba(var(--clinch-primary-rgb), 0.06));
}

.profile-avatar {
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, var(--clinch-blue-primary) 0%, var(--clinch-blue-refresh-dark) 100%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  flex-shrink: 0;
  color: #fff;
  box-shadow: 0 8px 20px rgba(var(--clinch-primary-rgb), 0.28);
}

.profile-info {
  flex: 1;
  min-width: 0;
}

.profile-name {
  font-weight: 600;
  font-size: 0.8125rem;
  margin-bottom: 0.125rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--sidebar-fg, var(--clinch-navy));
}

.profile-role {
  font-size: 0.75rem;
  color: var(--sidebar-fg-muted, #64748b);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.profile-store {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  max-width: 100%;
  margin-top: 0.3rem;
  padding: 0.125rem 0.5rem;
  border-radius: 999px;
  font-size: 0.6875rem;
  font-weight: 600;
  line-height: 1.35;
  color: var(--sidebar-active-fg, var(--clinch-navy));
  background: var(--sidebar-active-bg, rgba(var(--clinch-primary-rgb), 0.14));
}

.profile-store i {
  flex-shrink: 0;
  font-size: 0.625rem;
  opacity: 0.85;
}

.profile-store-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Pill — capsule chip with circular avatar (Platform theme option) */
.sidebar-profile--pill {
  margin: 0.55rem 0.65rem 0.65rem;
  padding: 0.35rem 0.7rem 0.35rem 0.35rem;
  border: 1px solid var(--sidebar-border, rgba(var(--clinch-navy-rgb), 0.12));
  border-radius: 999px;
  border-bottom: 1px solid var(--sidebar-border, rgba(var(--clinch-navy-rgb), 0.12));
  background: var(--sidebar-bg-start, #ffffff);
  box-shadow: none;
}

.sidebar-profile--pill .profile-avatar {
  width: 1.85rem;
  height: 1.85rem;
  border-radius: 999px;
  font-size: 0.8rem;
  box-shadow: none;
}

.sidebar-profile--pill .profile-name {
  font-size: 0.78rem;
  font-weight: 700;
  margin-bottom: 0.05rem;
}

.sidebar-profile--pill .profile-role {
  font-size: 0.68rem;
}

.sidebar-profile--pill .profile-store,
.sidebar-profile--minimal .profile-store {
  margin-top: 0.1rem;
  padding: 0;
  background: transparent;
  font-size: 0.64rem;
}

/* Card — inset rounded tile */
.sidebar-profile--card {
  margin: 0.55rem 0.65rem 0.65rem;
  padding: 0.65rem 0.7rem;
  border: 1px solid var(--sidebar-border, rgba(var(--clinch-navy-rgb), 0.1));
  border-radius: 0.85rem;
  border-bottom: 1px solid var(--sidebar-border, rgba(var(--clinch-navy-rgb), 0.1));
  background: var(--sidebar-hover-bg, rgba(var(--clinch-primary-rgb), 0.08));
}

.sidebar-profile--card .profile-avatar {
  border-radius: 0.65rem;
}

/* Minimal — no chrome, text only beside avatar */
.sidebar-profile--minimal {
  margin: 0.35rem 0.55rem 0.45rem;
  padding: 0.35rem 0.4rem;
  border: 0;
  border-bottom: 0;
  background: transparent;
}

.sidebar-profile--minimal .profile-avatar {
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 999px;
  font-size: 0.75rem;
  box-shadow: none;
}

/* Navigation — scroll only when expanded (labels + long lists) */
.sidebar-nav {
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0.625rem 0;
}

/* Collapsed rail: no internal scroll except Cards (captions + long icon lists) */
.modern-sidebar.collapsed .sidebar-nav {
  overflow: hidden;
  flex: 1 1 auto;
  padding: 0.5rem 0;
}

.modern-sidebar.sidebar-layout-cards.collapsed .sidebar-nav {
  overflow-x: hidden;
  overflow-y: auto;
  flex: 1 1 0;
  min-height: 0;
  overscroll-behavior: contain;
  scrollbar-width: thin;
}

.modern-sidebar.sidebar-layout-cards.collapsed .nav-list {
  padding-bottom: 0.85rem;
}

.nav-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.nav-item {
  padding: 0 0.625rem;
}

.nav-group {
  padding: 0 0.625rem;
  margin-top: 0.15rem;
}

.nav-group__toggle {
  display: flex;
  align-items: center;
  width: 100%;
  padding: var(--sidebar-item-padding, 0.45rem 0.75rem);
  color: var(--sidebar-fg-muted, #64748b);
  background: transparent;
  border: 1px solid transparent;
  border-radius: var(--sidebar-item-radius, 0.5rem);
  transition: all 0.3s ease;
  gap: 0.625rem;
  font-weight: 500;
  font-size: 0.8125rem;
  letter-spacing: 0;
  text-transform: none;
  min-height: 2.125rem;
  cursor: pointer;
  font-family: inherit;
  text-align: left;
}

.nav-group__toggle:hover {
  background: var(--sidebar-hover-bg, rgba(var(--clinch-primary-rgb), 0.1));
  color: var(--sidebar-fg, var(--clinch-navy));
}

.nav-group__toggle.is-active {
  background: transparent;
  color: var(--sidebar-fg, var(--clinch-navy, #001f3f));
  border-color: transparent;
  box-shadow: none;
  font-weight: 700;
}

.nav-group__toggle.is-active .nav-label {
  font-weight: 700;
}

.nav-group__toggle .nav-label {
  flex: 1;
}

.nav-group__toggle .nav-icon {
  position: relative;
}

.nav-alert-dot {
  width: 0.45rem;
  height: 0.45rem;
  margin-right: 0.2rem;
  border-radius: 999px;
  background: #dc2626;
  flex-shrink: 0;
}

.nav-alert-dot--on-icon {
  position: absolute;
  top: -0.1rem;
  right: -0.1rem;
  margin: 0;
  border: 1px solid var(--sidebar-bg, #fff);
}

/* Icon-top only: badge overlays the icon corner */
.nav-alert-badge {
  margin-left: auto;
  min-width: 1.25rem;
  height: 1.25rem;
  padding: 0 0.35rem;
  border-radius: 999px;
  background: #dc2626;
  color: #fff;
  font-size: 0.66rem;
  font-weight: 700;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.nav-alert-badge--on-icon {
  position: absolute;
  top: -0.45rem;
  right: -0.55rem;
  margin: 0;
  min-width: 1.05rem;
  height: 1.05rem;
  font-size: 0.58rem;
  border: 1px solid var(--sidebar-bg, #fff);
  z-index: 1;
  pointer-events: none;
}

.nav-group__chevron {
  margin-left: 0;
  font-size: 0.65rem;
  opacity: 0.7;
  transition: transform 0.2s ease;
}

.nav-group__chevron.is-open {
  transform: rotate(180deg);
}

.nav-group__items {
  list-style: none;
  padding: 0.15rem 0 0.35rem;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.modern-sidebar:not(.collapsed) .nav-group__items {
  position: relative;
  margin: 0.05rem 0 0.4rem 1.35rem;
  padding: 0.1rem 0 0.15rem 0.7rem;
  border-left: 1px solid var(--sidebar-line, rgba(100, 116, 139, 0.35));
  gap: 0;
}

.nav-item--nested {
  position: relative;
  padding: 0;
}

.modern-sidebar:not(.collapsed) .nav-item--nested::before {
  content: '';
  position: absolute;
  left: -0.7rem;
  top: 50%;
  width: 0.55rem;
  border-top: 1px solid var(--sidebar-line, rgba(100, 116, 139, 0.35));
}

.nav-item--nested .nav-link {
  padding-left: 1.15rem;
  min-height: 2.125rem;
  font-size: 0.8125rem;
  font-weight: 500;
}

.modern-sidebar:not(.collapsed) .nav-item--nested .nav-icon {
  width: 0.95rem;
  height: 0.95rem;
  font-size: 0.72rem;
}

.modern-sidebar:not(.collapsed) .nav-item--nested .nav-link {
  padding: 0.28rem 0.4rem;
  min-height: 1.7rem;
  border: 0;
  border-radius: 6px;
  box-shadow: none;
}

.modern-sidebar:not(.collapsed) .nav-item--nested .nav-link:hover {
  transform: none;
  box-shadow: none;
}

.modern-sidebar:not(.collapsed) .nav-item--nested .nav-link.active {
  transform: none;
  margin: 0.08rem 0.15rem 0.08rem 0;
  padding: 0.34rem 0.6rem;
  min-height: 1.9rem;
  border-radius: 8px;
  border: 1px solid rgba(var(--clinch-primary-rgb), 0.34);
  background: rgba(var(--clinch-primary-rgb), 0.16);
  color: var(--clinch-blue-refresh-dark, #0081c9);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
  font-weight: 600;
}

/* Icon-top: group headers match stacked items; counts sit on the icon */
.modern-sidebar.sidebar-layout-stack:not(.collapsed) .nav-group__toggle {
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  gap: 0.3rem;
  padding: 0.55rem 0.4rem;
  min-height: 3.25rem;
  width: 100%;
  box-sizing: border-box;
}

.modern-sidebar.sidebar-layout-stack:not(.collapsed) .nav-group__toggle .nav-label {
  flex: none;
  width: 100%;
  font-size: 0.68rem;
  font-weight: 500;
  line-height: 1.15;
  white-space: normal;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
}

.modern-sidebar.sidebar-layout-stack:not(.collapsed) .nav-group__chevron {
  display: none;
}

.modern-sidebar.sidebar-layout-stack:not(.collapsed) .nav-item--nested .nav-link {
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  text-align: left;
  gap: 0.625rem;
  padding: 0.45rem 0.75rem 0.45rem 1.15rem;
  min-height: 2.125rem;
  width: 100%;
}

.modern-sidebar.sidebar-layout-stack:not(.collapsed) .nav-item--nested .nav-label {
  flex: 1;
  width: auto;
  font-size: 0.8125rem;
  font-weight: 500;
  white-space: nowrap;
  display: block;
  -webkit-line-clamp: unset;
  line-height: 1.2;
}

.modern-sidebar.sidebar-layout-stack:not(.collapsed) .nav-item--nested .nav-icon {
  width: 0.95rem;
  height: 0.95rem;
  font-size: 0.72rem;
}

.modern-sidebar.sidebar-layout-stack:not(.collapsed) .nav-item--nested .nav-link {
  padding: 0.28rem 0.4rem;
  min-height: 1.7rem;
  border: 0;
  box-shadow: none;
}

.modern-sidebar.sidebar-layout-stack:not(.collapsed) .nav-item--nested .nav-link.active {
  padding: 0.34rem 0.6rem;
  min-height: 1.9rem;
  border: 1px solid rgba(var(--clinch-primary-rgb), 0.34);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
  background: rgba(var(--clinch-primary-rgb), 0.16);
  color: var(--clinch-blue-refresh-dark, #0081c9);
  font-weight: 600;
}

.nav-link {
  display: flex;
  align-items: center;
  padding: var(--sidebar-item-padding, 0.45rem 0.75rem);
  color: var(--sidebar-fg-muted, #64748b);
  text-decoration: none;
  border: 1px solid transparent;
  border-radius: var(--sidebar-item-radius, 0.5rem);
  transition: all 0.3s ease;
  position: relative;
  gap: 0.625rem;
  font-weight: 500;
  font-size: 0.8125rem;
  min-height: 2.125rem;
}

.nav-link:hover {
  background: var(--sidebar-hover-bg, rgba(var(--clinch-primary-rgb), 0.1));
  color: var(--sidebar-fg, var(--clinch-navy));
  transform: translateX(4px);
}

:global(html[data-sidebar-style='ink']) .nav-link:hover,
:global(html[data-sidebar-style='slate']) .nav-link:hover,
:global(html[data-sidebar-style='outline']) .nav-link:hover,
:global(html[data-sidebar-style='graphite']) .nav-link:hover {
  transform: none;
}

.nav-link.active {
  background: transparent;
  color: var(--sidebar-fg, var(--clinch-navy, #001f3f));
  border-color: transparent;
  box-shadow: none;
  font-weight: 700;
}

.nav-link.active .nav-label {
  font-weight: 700;
}

.nav-link.active::before,
.nav-link.active .nav-indicator {
  display: none;
}

/* Icon on top, label below */
.modern-sidebar.sidebar-layout-stack:not(.collapsed) .nav-link {
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  gap: 0.3rem;
  padding: 0.55rem 0.4rem;
  min-height: 3.25rem;
  width: 100%;
  box-sizing: border-box;
}

.modern-sidebar.sidebar-layout-stack:not(.collapsed) .nav-item {
  padding: 0 0.55rem;
}

.modern-sidebar.sidebar-layout-stack:not(.collapsed) .nav-group {
  padding: 0 0.55rem;
}

.modern-sidebar.sidebar-layout-stack:not(.collapsed) .nav-group {
  padding: 0 0.55rem;
}

.modern-sidebar.sidebar-layout-stack:not(.collapsed) .nav-link:hover {
  transform: none;
}

.modern-sidebar.sidebar-layout-stack:not(.collapsed) .nav-icon {
  width: 1.5rem;
  height: 1.5rem;
  font-size: 1.1rem;
  overflow: visible;
}

.modern-sidebar.sidebar-layout-stack:not(.collapsed) .nav-label {
  flex: none;
  width: 100%;
  font-size: 0.68rem;
  line-height: 1.15;
  white-space: normal;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
}

.modern-sidebar.sidebar-layout-stack:not(.collapsed) .nav-indicator {
  display: none;
}

.modern-sidebar.sidebar-layout-stack:not(.collapsed) .nav-link.active::before {
  left: 50%;
  top: auto;
  bottom: 0;
  transform: translateX(-50%);
  width: 1.35rem;
  height: 3px;
  border-radius: 4px 4px 0 0;
}

.modern-sidebar.sidebar-layout-stack:not(.collapsed) .logo-container {
  padding: 0.125rem 0.2rem;
}

.modern-sidebar.sidebar-layout-stack:not(.collapsed) .sidebar-nav {
  padding-left: 0.15rem;
  padding-right: 0.15rem;
  padding-bottom: 0.75rem;
}

.modern-sidebar.sidebar-layout-stack:not(.collapsed) .sidebar-footer {
  padding: 0.75rem 0.55rem 1.15rem;
  margin-bottom: 0.35rem;
}

.modern-sidebar.sidebar-layout-stack:not(.collapsed) {
  padding-bottom: 0.75rem;
  border-left: 1px solid var(--sidebar-border, rgba(var(--clinch-navy-rgb), 0.12));
  border-right: 1px solid var(--sidebar-border, rgba(var(--clinch-navy-rgb), 0.12));
}

.modern-sidebar.sidebar-layout-stack:not(.collapsed) .footer-btn {
  flex-direction: column;
  justify-content: center;
  gap: 0.2rem;
  padding: 0.45rem 0.35rem;
  min-height: 2.75rem;
  width: 100%;
  box-sizing: border-box;
}

.modern-sidebar.sidebar-layout-stack:not(.collapsed) .footer-label {
  font-size: 0.65rem;
  text-align: center;
  white-space: normal;
  line-height: 1.1;
}

/* Slim / compact: same inline row as default, narrower rail */
.modern-sidebar.sidebar-layout-compact:not(.collapsed) .sidebar-header {
  padding: 0.65rem 0.55rem;
}

.modern-sidebar.sidebar-layout-compact:not(.collapsed) .logo-container {
  padding: 0.15rem 0.1rem;
}

.modern-sidebar.sidebar-layout-compact:not(.collapsed) .sidebar-profile {
  padding: 0.65rem 0.55rem;
  gap: 0.45rem;
}

.modern-sidebar.sidebar-layout-compact:not(.collapsed) .profile-avatar {
  width: 30px;
  height: 30px;
  font-size: 0.85rem;
  border-radius: 8px;
}

.modern-sidebar.sidebar-layout-compact:not(.collapsed) .profile-name {
  font-size: 0.75rem;
}

.modern-sidebar.sidebar-layout-compact:not(.collapsed) .profile-role {
  font-size: 0.65rem;
}

.modern-sidebar.sidebar-layout-compact:not(.collapsed) .profile-store {
  margin-top: 0.2rem;
  padding: 0.0625rem 0.4rem;
  font-size: 0.6rem;
}

.modern-sidebar.sidebar-layout-compact:not(.collapsed) .nav-item,
.modern-sidebar.sidebar-layout-compact:not(.collapsed) .nav-group {
  padding: 0 0.4rem;
}

.modern-sidebar.sidebar-layout-compact:not(.collapsed) .nav-link,
.modern-sidebar.sidebar-layout-compact:not(.collapsed) .nav-group__toggle {
  padding: 0.4rem 0.5rem;
  gap: 0.45rem;
  min-height: 2rem;
  font-size: 0.75rem;
}

.modern-sidebar.sidebar-layout-compact:not(.collapsed) .nav-icon {
  width: 1.2rem;
  height: 1.2rem;
  font-size: 0.9rem;
}

.modern-sidebar.sidebar-layout-compact:not(.collapsed) .nav-label {
  font-size: 0.75rem;
}

.modern-sidebar.sidebar-layout-compact:not(.collapsed) .nav-item--nested .nav-link {
  padding-left: 0.85rem;
  min-height: 1.9rem;
  font-size: 0.72rem;
}

.modern-sidebar.sidebar-layout-compact:not(.collapsed) .sidebar-footer {
  padding: 0.65rem 0.45rem 1rem;
}

.modern-sidebar.sidebar-layout-compact:not(.collapsed) .footer-btn {
  padding: 0.4rem 0.5rem;
  gap: 0.4rem;
  min-height: 2rem;
}

.modern-sidebar.sidebar-layout-compact:not(.collapsed) .logout-btn--icon {
  width: 2rem;
  min-width: 2rem;
  height: 2rem;
  min-height: 2rem;
  padding: 0;
}

.modern-sidebar.sidebar-layout-compact:not(.collapsed) .footer-label {
  font-size: 0.72rem;
}

.nav-link.active::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 1.125rem;
  background: linear-gradient(180deg, var(--clinch-blue-primary) 0%, var(--clinch-blue-refresh-dark) 100%);
  border-radius: 0 4px 4px 0;
  opacity: var(--sidebar-indicator-opacity, 1);
}

.nav-icon {
  position: relative;
  width: 1.375rem;
  height: 1.375rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 1rem;
}

.nav-label {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.nav-indicator {
  width: 6px;
  height: 6px;
  background: var(--clinch-blue-primary);
  border-radius: 50%;
  flex-shrink: 0;
  box-shadow: 0 0 8px rgba(var(--clinch-primary-rgb), 0.45);
  opacity: var(--sidebar-indicator-opacity, 1);
}

/* Collapsed State — icon rail: larger glyphs, comfortable tap targets */
.modern-sidebar.collapsed .nav-list {
  gap: 0.2rem;
}

.modern-sidebar.collapsed .nav-item {
  padding: 0 0.5rem;
}

.modern-sidebar.collapsed .nav-link {
  padding: 0.4rem 0.35rem;
  justify-content: center;
  transform: none;
  min-height: 2.25rem;
}

.modern-sidebar.collapsed .nav-icon {
  width: 1.75rem;
  height: 1.75rem;
  font-size: 1.1rem;
}

.modern-sidebar.collapsed .nav-link:hover {
  transform: none;
}

.modern-sidebar.collapsed .nav-label,
.modern-sidebar.collapsed .nav-indicator {
  display: none;
}

/* Cards rail: 8px captions sit in existing padding so icon boxes stay put */
.modern-sidebar.sidebar-layout-cards.collapsed .nav-link {
  overflow: visible;
}

.modern-sidebar.sidebar-layout-cards.collapsed .nav-label.nav-label--rail {
  display: block;
  position: absolute;
  left: 2px;
  right: 2px;
  top: calc(0.4rem + 1.75rem);
  flex: none;
  width: auto;
  margin: 0;
  padding: 0;
  font-size: 8px;
  font-weight: 600;
  line-height: 1;
  letter-spacing: -0.02em;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  pointer-events: none;
  opacity: 0.78;
}

.modern-sidebar.collapsed .nav-link.active::before {
  display: none;
}

/* Tooltip */
.nav-tooltip {
  position: absolute;
  left: calc(100% + 1rem);
  top: 50%;
  transform: translateY(-50%);
  background: var(--clinch-navy);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  white-space: nowrap;
  font-size: 0.9rem;
  font-weight: 500;
  box-shadow: 0 8px 24px rgba(var(--clinch-navy-rgb), 0.2);
  z-index: 1001;
  pointer-events: none;
}

.nav-tooltip::before {
  content: '';
  position: absolute;
  right: 100%;
  top: 50%;
  transform: translateY(-50%);
  border: 6px solid transparent;
  border-right-color: var(--clinch-navy);
}

.modern-sidebar:not(.collapsed) .nav-tooltip {
  display: none;
}

/* Tooltip Transitions */
.tooltip-enter-active,
.tooltip-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.tooltip-enter-from,
.tooltip-leave-to {
  opacity: 0;
  transform: translateY(-50%) translateX(-8px);
}

/* Scrollbar */
.sidebar-nav::-webkit-scrollbar {
  width: 6px;
}

.sidebar-nav::-webkit-scrollbar-track {
  background: rgba(var(--clinch-navy-rgb), 0.04);
}

.sidebar-nav::-webkit-scrollbar-thumb {
  background: rgba(var(--clinch-navy-rgb), 0.15);
  border-radius: 3px;
}

.sidebar-nav::-webkit-scrollbar-thumb:hover {
  background: rgba(var(--clinch-navy-rgb), 0.28);
}

.modern-sidebar.sidebar-layout-cards.collapsed .sidebar-nav::-webkit-scrollbar {
  width: 4px;
}

/* Footer */
.sidebar-footer {
  padding: 0.625rem 0.625rem;
  border-top: 1px solid var(--sidebar-border, rgba(var(--clinch-navy-rgb), 0.08));
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  flex-shrink: 0;
}

.sidebar-footer--identity {
  flex-direction: row;
  align-items: center;
  gap: 0.45rem;
  padding: 0.55rem 0.7rem 0.7rem;
}

.sidebar-footer-identity {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  min-width: 0;
  flex: 1;
}

.sidebar-footer-identity .profile-avatar {
  width: 2rem;
  height: 2rem;
  border-radius: 999px;
  font-size: 0.85rem;
  box-shadow: none;
}

.sidebar-footer-identity .profile-info {
  min-width: 0;
}

.sidebar-footer-identity .profile-name {
  font-size: 0.8125rem;
  font-weight: 700;
  margin-bottom: 0;
}

.sidebar-footer-identity .profile-role {
  font-size: 0.6875rem;
}

.sidebar-footer-identity .profile-store {
  margin-top: 0.15rem;
  padding: 0;
  background: transparent;
  font-size: 0.64rem;
}

.logout-btn--icon {
  width: 2.15rem;
  min-width: 2.15rem;
  height: 2.15rem;
  min-height: 2.15rem;
  padding: 0;
  justify-content: center;
  border-radius: 999px;
  flex-shrink: 0;
}

.logout-btn--icon .footer-icon {
  width: auto;
  height: auto;
}

.logout-btn--icon:hover {
  transform: none;
}

.footer-btn {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.45rem 0.75rem;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(var(--clinch-navy-rgb), 0.1);
  border-radius: 0.5rem;
  color: var(--clinch-navy);
  cursor: pointer;
  transition: all 0.3s ease;
  gap: 0.625rem;
  font-size: 0.8125rem;
  min-height: 2.125rem;
  position: relative;
  font-weight: 500;
  font-family: 'Outfit', sans-serif;
  box-shadow: 0 2px 8px rgba(var(--clinch-navy-rgb), 0.04);
}

.footer-btn:hover {
  background: rgba(var(--clinch-primary-rgb), 0.08);
  border-color: rgba(var(--clinch-primary-rgb), 0.25);
  transform: translateY(-2px);
  box-shadow: 0 8px 18px rgba(var(--clinch-navy-rgb), 0.08);
}

.footer-btn:active {
  transform: translateY(0);
}

.logout-btn {
  background: rgba(248, 113, 113, 0.08);
  border-color: rgba(248, 113, 113, 0.22);
  color: var(--sidebar-logout, #b91c1c);
}

.logout-btn:hover {
  background: rgba(248, 113, 113, 0.14);
  border-color: rgba(248, 113, 113, 0.32);
}

.footer-icon {
  width: 1.375rem;
  height: 1.375rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 1rem;
}

.footer-label {
  flex: 1;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.modern-sidebar.collapsed .sidebar-footer {
  padding: 0.5rem 0.35rem;
  gap: 0.25rem;
  flex-shrink: 0;
}

.modern-sidebar.collapsed .footer-btn {
  padding: 0.4rem 0.35rem;
  justify-content: center;
  min-height: 2.25rem;
}

.modern-sidebar.collapsed .footer-icon {
  width: 1.75rem;
  height: 1.75rem;
  font-size: 1.1rem;
}

.modern-sidebar.collapsed .footer-label {
  display: none;
}

.modern-sidebar.sidebar-layout-cards.collapsed .footer-btn {
  overflow: visible;
}

.modern-sidebar.sidebar-layout-cards.collapsed .footer-label.footer-label--rail {
  display: block;
  position: absolute;
  left: 2px;
  right: 2px;
  top: calc(0.4rem + 1.75rem);
  flex: none;
  font-size: 8px;
  font-weight: 600;
  line-height: 1;
  letter-spacing: -0.02em;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  pointer-events: none;
  opacity: 0.78;
}

.modern-sidebar.collapsed .footer-btn:hover {
  transform: none;
}

.modern-sidebar.sidebar-layout-sections:not(.collapsed) .logo-container,
.modern-sidebar.sidebar-layout-dock:not(.collapsed) .logo-container,
.modern-sidebar.sidebar-style-ink:not(.collapsed) .logo-container,
.modern-sidebar.sidebar-style-slate:not(.collapsed) .logo-container {
  justify-content: flex-start;
  padding-left: 0.15rem;
}

.modern-sidebar.sidebar-layout-sections:not(.collapsed) .nav-group__toggle.is-kicker {
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-size: 0.6875rem;
  font-weight: 700;
  color: var(--sidebar-fg-muted, #64748b);
  background: transparent;
  padding: 0.9rem 0.7rem 0.2rem;
  min-height: 0;
  cursor: default;
  pointer-events: none;
}

.modern-sidebar.sidebar-layout-sections:not(.collapsed) .nav-group__toggle.is-kicker .nav-icon,
.modern-sidebar.sidebar-layout-sections:not(.collapsed) .nav-group__toggle.is-kicker .nav-alert-badge {
  display: none;
}

.modern-sidebar.sidebar-layout-sections:not(.collapsed) .nav-group {
  margin-top: 0.35rem;
}

.modern-sidebar.sidebar-layout-sections:not(.collapsed) .nav-item--nested .nav-link {
  padding-left: 0.75rem;
}

.modern-sidebar.sidebar-layout-dock:not(.collapsed) .sidebar-nav,
.modern-sidebar.sidebar-layout-sections:not(.collapsed) .sidebar-nav {
  padding: 0.4rem 0 0.75rem;
}

.modern-sidebar.sidebar-layout-dock:not(.collapsed) .nav-item,
.modern-sidebar.sidebar-layout-dock:not(.collapsed) .nav-group,
.modern-sidebar.sidebar-layout-sections:not(.collapsed) .nav-item,
.modern-sidebar.sidebar-layout-sections:not(.collapsed) .nav-group {
  padding: 0 0.7rem;
}

.modern-sidebar.sidebar-style-outline:not(.collapsed) .nav-list {
  gap: 0.35rem;
}

.modern-sidebar.sidebar-style-outline:not(.collapsed) .nav-item,
.modern-sidebar.sidebar-style-outline:not(.collapsed) .nav-group {
  padding: 0 0.9rem;
}

.modern-sidebar.sidebar-style-slate:not(.collapsed) .sidebar-nav {
  margin: 0 0.45rem 0.45rem;
  padding: 0.35rem 0;
  border-radius: 0.85rem;
  background: rgba(255, 255, 255, 0.45);
}

.modern-sidebar.sidebar-style-graphite:not(.collapsed) .sidebar-nav {
  padding: 0.35rem 0;
}

.modern-sidebar.sidebar-layout-compact:not(.collapsed) .nav-label {
  font-size: 0.72rem;
}

/* Mobile Overlay */
.sidebar-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  z-index: 999;
}

.overlay-enter-active,
.overlay-leave-active {
  transition: opacity 0.3s ease;
}

.overlay-enter-from,
.overlay-leave-to {
  opacity: 0;
}

/* Mobile Styles */
@media (max-width: 768px) {
  .modern-sidebar {
    transform: translateX(-100%);
    width: 280px;
    z-index: 1002;
    box-shadow: 4px 0 32px rgba(0, 0, 0, 0.3);
  }
  
  .modern-sidebar.mobile-open {
    transform: translateX(0);
  }
  
  .modern-sidebar.collapsed {
    transform: translateX(-100%);
    width: 280px;
  }
  
  /* Ensure profile section shows on mobile */
  .sidebar-profile {
    display: flex;
  }
}

/* Smooth Animations */
@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.nav-item {
  animation: slideIn 0.3s ease-out;
  animation-fill-mode: both;
}

.nav-item:nth-child(1) { animation-delay: 0.05s; }
.nav-item:nth-child(2) { animation-delay: 0.1s; }
.nav-item:nth-child(3) { animation-delay: 0.15s; }
.nav-item:nth-child(4) { animation-delay: 0.2s; }
.nav-item:nth-child(5) { animation-delay: 0.25s; }
.nav-item:nth-child(6) { animation-delay: 0.3s; }

/* Focus States for Accessibility */
.nav-link:focus-visible,
.nav-group__toggle:focus-visible,
.footer-btn:focus-visible,
.expand-btn:focus-visible,
.mobile-close-btn:focus-visible {
  outline: 2px solid var(--clinch-blue-primary);
  outline-offset: 2px;
}

</style>